import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'

import { type GridColDef, Pagination } from '@dhl/components'
import { type OrganisationDto, useGetBillingUnitOrganisations } from '@dhl/mdp-api'
import { Box, Card } from '@dhl-ui/components'
import { AdminDataGrid, useSearchAndPage } from '~/common'

type OrganisationsProps = {
  id?: string
}

export const Organisations = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { page, pageSize, setPage, setPageSize } = useSearchAndPage()
  const { id: billingUnitId } = useParams<OrganisationsProps>()

  const { data: billingUnitOrganisations, isFetching } = useGetBillingUnitOrganisations(billingUnitId ?? '')

  const renderCellCity = (row: OrganisationDto) => {
    return row?.data?.address?.city ? row?.data?.address?.city : ''
  }
  const renderCellCountry = (row: OrganisationDto) => row?.data?.address?.countryCode

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('admin.billingUnitsPage.billingUnitsForm.name'),
      flex: 1,
    },
    {
      field: 'city',
      headerName: t('admin.billingUnitsPage.billingUnitsForm.city'),
      flex: 1,
      valueGetter: (value, row) => renderCellCity(row),
    },
    {
      field: 'countryCode',
      headerName: t('admin.billingUnitsPage.billingUnitsForm.country'),
      flex: 1,
      valueGetter: (value, row) => renderCellCountry(row),
    },
  ]

  if (isFetching || !billingUnitOrganisations) return null

  return (
    <Card>
      <Card.Header title={t('admin.billingUnitsPage.organisation')} />
      <Card.Content>
        <AdminDataGrid
          loading={isFetching}
          data-testid="billingunit-table"
          sx={{ '& .MuiDataGrid-cell': { cursor: 'default' } }}
          autoHeight
          rows={billingUnitOrganisations || []}
          columns={columns}
          paginationModel={{
            page,
            pageSize,
          }}
          rowHeight={48}
          columnHeaderHeight={48}
          disableRowSelectionOnClick
          disableColumnFilter
          hideFooter
          onRowClick={({ id: organizationId }) =>
            navigate(`/organisations/details/${organizationId}?billingunit=${billingUnitId}`)
          }
        />
        <Box px={2} py={1}>
          <Pagination
            page={page}
            count={(billingUnitOrganisations || []).length}
            onPageChange={(_, page) => setPage(page)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={setPageSize}
          />
        </Box>
      </Card.Content>
    </Card>
  )
}
